import React, { useMemo, useState } from "react";
import type { Column, Table } from "@tanstack/react-table";
import { IconButton, CheckBox, TextInput, TextButton } from "../../../controls";
import SeemPopover from "../../SeemPopover/SeemPopover";
import Loader from "../../Loader/Loader";

// columnDef carries non-standard fields (`title`, `hideOnEdit`) added by the
// app's table definitions on top of TanStack's ColumnDef.
type ColumnDefWithExtras = {
  header?: unknown;
  title?: string;
  accessorKey?: string;
  hideOnEdit?: boolean;
};

type TableColumn = Column<unknown, unknown> & {
  columnDef: ColumnDefWithExtras;
};

interface ColumnOption extends TableColumn {
  label?: string;
  isDisabled: boolean;
}

interface ColumnChoosingPopoverProps {
  table: Table<unknown>;
  resetColumns: () => void;
}

const ColumnChoosingPopover: React.FC<ColumnChoosingPopoverProps> = ({
  table,
  resetColumns,
}) => {
  const [isOpen, toggleIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const columOptions = useMemo<ColumnOption[]>(
    () =>
      ((table?.getAllLeafColumns() as TableColumn[] | undefined)
        ?.map((column) => ({
          ...column,
          label:
            typeof column?.columnDef?.header === "string"
              ? column.columnDef?.header
              : column.columnDef?.title || column.columnDef?.accessorKey,
          isDisabled: !column?.getCanHide(),
        }))
        .filter((column) => !column.columnDef?.hideOnEdit) ?? []) as ColumnOption[],
    [table.getAllLeafColumns()?.length, isOpen]
  );

  const displayedColumns = useMemo(() => {
    const list = columOptions.filter((c) =>
      c.label?.toLowerCase()?.includes(searchTerm?.toLowerCase())
    );

    const disabled = list
      .filter((c) => c.isDisabled)
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
    const selected = list
      .filter((c) => c.getIsVisible() && !c.isDisabled)
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));
    const unselected = list
      .filter((c) => !c.getIsVisible() && !c.isDisabled)
      .sort((a, b) => (a.label ?? "").localeCompare(b.label ?? ""));

    return [...disabled, ...selected, ...unselected];
  }, [columOptions, searchTerm]);

  const handleToggle = (column: ColumnOption, newValue: boolean) => {
    column.getToggleVisibilityHandler()({
      target: { checked: newValue },
    });

    if (!!newValue) {
      table?.setColumnOrder((prev) => [
        ...prev,
        column?.columnDef?.accessorKey as string,
      ]);
    } else {
      table?.setColumnOrder((prev) =>
        prev.filter((key) => key !== column?.columnDef?.accessorKey)
      );
    }
  };

  return (
    <>
      <SeemPopover
        isOpen={isOpen}
        placement={"bottom"}
        body={
          <div className={"column-options-wrapper"}>
            <TextInput
              wrapperClassName={"table-column-search no-padding-bottom"}
              data-testid={"table-columns-search"}
              placeholder={"Search columns..."}
              isClearable
              leftIcon={"seem-icon-search"}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e?.target?.value)}
              applyAutoFocus
            />
            <div className="reset-columns-button-wrapper">
              <TextButton
                label={"Reset to Default"}
                leftIcon={"seem-icon-reset"}
                buttonStyle={"textLink"}
                className={"reset-columns-button"}
                onClick={() => {
                  resetColumns();
                  setSearchTerm("");
                  toggleIsOpen(false);
                }}
              />
            </div>

            {!!displayedColumns?.length ? (
              <ul
                className={
                  "column-options-list input-wrap checkbox-list-wrapper has-background in-dropdown"
                }
              >
                <div className="checkbox-list-options-container">
                  {displayedColumns?.map((column) => {
                    const isChecked = column.getIsVisible();
                    return (
                      <li
                        key={`column-option-${column.id}`}
                        className={`checkbox-list-option  ${
                          isChecked ? "selected" : ""
                        }`}
                      >
                        <CheckBox
                          data-testid={`column-option-${column.id}`}
                          label={column.label}
                          checked={isChecked}
                          onChange={(_, newValue) => {
                            handleToggle(column, newValue);
                          }}
                          disabled={column.isDisabled}
                          noMargin
                          withBg
                          withTooltip={
                            column.isDisabled && {
                              content:
                                "This column is system-provided and cannot be removed",
                              classname: "default-column-tooltip",
                              delayShow: 200,
                            }
                          }
                        />
                      </li>
                    );
                  })}
                </div>
              </ul>
            ) : (
              <Loader
                center
                title={"No results"}
                subtitle={"Try adjusting your search"}
              />
            )}
          </div>
        }
        onOuterAction={() => setTimeout(() => toggleIsOpen(false), 200)}
      >
        <IconButton
          isIcon={"seem-icon-column-selecting"}
          className={"column-choosing-button"}
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            toggleIsOpen((prev) => !prev);
          }}
          data-testid={"column-choosing-button"}
          withTooltip={{ content: "Select Columns" }}
        />
      </SeemPopover>
    </>
  );
};

export default ColumnChoosingPopover;
